import { Link } from "react-router-dom";
import { useState } from "react";
import {
    ArrowUpRight,
    ArrowRight,
    Search,
    Users,
    Handshake,
    Sparkles,
    Building2,
    UserCheck,
    Target,
    Star,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";
import T from "../components/EditableText";

const HERO_BG =
    "https://images.pexels.com/photos/12707786/pexels-photo-12707786.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const STEPS = [
    {
        n: "01",
        icon: Search,
        title: "Briefing & Strategie",
        text: "Wir verstehen Ihre Kultur, Ihr Team und die echte Aufgabe — nicht nur die Stellenbeschreibung.",
    },
    {
        n: "02",
        icon: Users,
        title: "AI-Powered Sourcing",
        text: "Unsere Datenbasis kombiniert mit echtem Headhunter-Handwerk findet die 1% — auch passive Talente.",
    },
    {
        n: "03",
        icon: Handshake,
        title: "Match & Closing",
        text: "Strukturierte Interviews, Reference Checks, Vertragsverhandlung. Wir sind dabei bis zur Unterschrift.",
    },
];

const SERVICES = [
    {
        icon: Target,
        title: "Executive Search",
        text: "C-Level und VP-Rollen. Diskret, gründlich, dauerhaft.",
        tag: "Retained",
    },
    {
        icon: Building2,
        title: "Tech & Engineering",
        text: "Senior Engineers, Tech Leads und Heads — von Berlin bis Lissabon.",
        tag: "Specialized",
    },
    {
        icon: UserCheck,
        title: "Sales & Growth",
        text: "Pipeline-fähige Sales-Talente, die Quoten übertreffen.",
        tag: "Performance",
    },
];

const TESTIMONIALS = [
    {
        quote:
            "aimploy hat in 6 Wochen unseren VP Engineering platziert — drei Headhunter haben es vorher in 6 Monaten nicht geschafft.",
        name: "Lara Sommer",
        role: "Co-Founder, Series-B SaaS",
    },
    {
        quote:
            "Der Prozess war strukturiert, transparent und schnell. Die Qualität der Kandidaten war auf einem anderen Level.",
        name: "Maximilian Roth",
        role: "Chief People Officer",
    },
    {
        quote:
            "Endlich ein Recruiting-Partner, der Tech versteht. Keine durchgereichten Lebensläufe, sondern echte Matches.",
        name: "Aylin Kaya",
        role: "Head of Engineering",
    },
];

export default function Home() {
    const [tIdx, setTIdx] = useState(0);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative bg-aimploy-dark text-white overflow-hidden grain">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-b from-aimploy-dark/60 via-aimploy-dark/85 to-aimploy-dark" />
                </div>
                <div className="absolute -bottom-40 -left-20 h-[500px] w-[500px] rounded-full bg-aimploy-violet/20 blur-3xl" />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-24 pb-32 lg:pt-32 lg:pb-40">
                    <div className="max-w-4xl">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs uppercase tracking-[0.2em] font-semibold backdrop-blur-md">
                            <Sparkles className="w-3 h-3 text-aimploy-violet" /> <T k="home.hero.badge">AI-driven Headhunting · Berlin</T>
                        </span>
                        <h1
                            data-testid="home-hero-title"
                            className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[0.95] tracking-tight mt-8"
                        >
                            <T k="home.hero.line1">Wir finden die</T>
                            <br />
                            <T k="home.hero.line2">Köpfe, die andere</T>
                            <br />
                            <T k="home.hero.line3" className="italic font-medium text-aimploy-violet">übersehen.</T>
                        </h1>
                        <T k="home.hero.subtitle" as="p" className="text-lg lg:text-xl text-white/70 max-w-2xl mt-8 leading-relaxed">
                            aimploy ist die nächste Generation Headhunting. Wir kombinieren tiefes Branchen-Verständnis mit KI-gestütztem Sourcing — und besetzen Schlüsselrollen in unter 8 Wochen.
                        </T>
                        <div className="flex flex-col sm:flex-row gap-4 mt-10">
                            <Link
                                to="/unternehmen"
                                data-testid="home-hero-cta-primary"
                                className="inline-flex items-center justify-center gap-2 bg-white text-aimploy-navy px-8 py-4 rounded-full text-base font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all"
                            >
                                <T k="home.hero.cta_primary">Talent finden</T>
                                <ArrowUpRight className="w-5 h-5" />
                            </Link>
                            <Link
                                to="/kandidaten"
                                data-testid="home-hero-cta-secondary"
                                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 px-8 py-4 rounded-full text-base font-semibold backdrop-blur-md transition-all"
                            >
                                <T k="home.hero.cta_secondary">Als Kandidat bewerben</T>
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>

                        {/* Floating stats */}
                        <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl">
                            {[
                                ["500+", "Erfolgreiche Hires"],
                                ["8 Wo.", "Ø Time-to-Hire"],
                                ["98%", "Retention nach 12 Monaten"],
                            ].map(([n, l], i) => (
                                <div key={l}>
                                    <div className="font-display text-3xl lg:text-4xl font-bold"><T k={`home.stat.${i}.value`}>{n}</T></div>
                                    <T k={`home.stat.${i}.label`} as="div" className="text-xs text-white/50 mt-1">{l}</T>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end mb-20">
                        <div className="lg:col-span-7">
                            <T k="home.how.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">
                                So arbeiten wir
                            </T>
                            <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] tracking-tight mt-4">
                                <T k="home.how.title">{`Drei Schritte. Ein Hire,\nder bleibt.`}</T>
                            </h2>
                        </div>
                        <T k="home.how.description" as="p" className="lg:col-span-5 text-aimploy-muted text-lg leading-relaxed">
                            Wir verschwenden keine Zeit mit Stellenanzeigen oder Bewerbungs-Massengräbern. Stattdessen ein klarer Prozess — transparent, strukturiert, schnell.
                        </T>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.n}
                                className="group relative bg-white rounded-2xl p-8 lg:p-10 border border-black/5 hover:border-aimploy-violet/30 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(26,26,46,0.06)]"
                            >
                                <div className="flex items-start justify-between mb-12">
                                    <div className="h-12 w-12 rounded-xl bg-aimploy-navy text-white flex items-center justify-center group-hover:bg-aimploy-violet transition-colors">
                                        <s.icon className="w-5 h-5" />
                                    </div>
                                    <span className="font-display text-5xl font-bold text-aimploy-navy/10">{s.n}</span>
                                </div>
                                <h3 className="font-display text-2xl font-bold mb-3"><T k={`home.step.${i}.title`}>{s.title}</T></h3>
                                <T k={`home.step.${i}.text`} as="p" className="text-aimploy-muted leading-relaxed">{s.text}</T>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 lg:py-32 bg-aimploy-dark text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-dots-dark opacity-40" />
                <div className="absolute top-1/2 -right-40 h-[500px] w-[500px] rounded-full bg-aimploy-violet/20 blur-3xl" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="max-w-3xl mb-20">
                        <T k="home.services.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Services</T>
                        <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                            <T k="home.services.title">{`Spezialisiert auf\ndie Rollen, die zählen.`}</T>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => (
                            <div
                                key={s.title}
                                className="group bg-white/[0.04] hover:bg-white/[0.08] backdrop-blur-md border border-white/10 rounded-2xl p-8 lg:p-10 transition-all hover:-translate-y-1"
                            >
                                <div className="flex items-center justify-between mb-12">
                                    <div className="h-12 w-12 rounded-xl bg-aimploy-violet/15 text-aimploy-violet flex items-center justify-center">
                                        <s.icon className="w-5 h-5" />
                                    </div>
                                    <T k={`home.service.${i}.tag`} className="text-xs uppercase tracking-[0.2em] text-white/50">{s.tag}</T>
                                </div>
                                <h3 className="font-display text-2xl font-bold mb-3"><T k={`home.service.${i}.title`}>{s.title}</T></h3>
                                <T k={`home.service.${i}.text`} as="p" className="text-white/60 leading-relaxed mb-8">{s.text}</T>
                                <Link to="/unternehmen" className="inline-flex items-center gap-1 text-sm font-semibold link-underline">
                                    <T k="home.service.cta">Mehr erfahren</T> <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-24 lg:py-32 bg-aimploy-bg">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
                        <div className="lg:col-span-8">
                            <T k="home.testimonials.eyebrow" className="text-xs uppercase tracking-[0.2em] text-aimploy-violet font-semibold">Stimmen</T>
                            <h2 className="font-display text-4xl lg:text-6xl font-bold leading-[0.95] mt-4">
                                <T k="home.testimonials.title">Was unsere Kunden sagen.</T>
                            </h2>
                        </div>
                        <div className="lg:col-span-4 flex gap-3 lg:justify-end">
                            <button
                                onClick={() => setTIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                                className="h-12 w-12 rounded-full border border-aimploy-navy/15 hover:bg-aimploy-navy hover:text-white flex items-center justify-center transition-all"
                                aria-label="Vorheriges"
                                data-testid="testimonial-prev"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setTIdx((i) => (i + 1) % TESTIMONIALS.length)}
                                className="h-12 w-12 rounded-full bg-aimploy-navy text-white hover:bg-aimploy-navy-hover flex items-center justify-center transition-all"
                                aria-label="Nächstes"
                                data-testid="testimonial-next"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="bg-white rounded-3xl p-10 lg:p-16 border border-black/5 relative overflow-hidden">
                        <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-aimploy-violet/10 blur-2xl" />
                        <div className="flex gap-1 mb-8">
                            {[...Array(5)].map((_, i) => (
                                <Star key={i} className="w-5 h-5 fill-aimploy-violet text-aimploy-violet" />
                            ))}
                        </div>
                        <blockquote
                            data-testid="testimonial-quote"
                            className="font-display text-2xl lg:text-4xl font-bold leading-[1.15] tracking-tight max-w-4xl"
                        >
                            "<T k={`home.testimonial.${tIdx}.quote`}>{TESTIMONIALS[tIdx].quote}</T>"
                        </blockquote>
                        <div className="mt-12 flex items-center gap-4">
                            <div className="h-14 w-14 rounded-full bg-aimploy-navy text-white flex items-center justify-center font-display font-bold">
                                {TESTIMONIALS[tIdx].name.split(" ").map((p) => p[0]).join("")}
                            </div>
                            <div>
                                <div className="font-semibold"><T k={`home.testimonial.${tIdx}.name`}>{TESTIMONIALS[tIdx].name}</T></div>
                                <div className="text-sm text-aimploy-muted"><T k={`home.testimonial.${tIdx}.role`}>{TESTIMONIALS[tIdx].role}</T></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24 lg:py-32 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
                    <h2 className="font-display text-5xl lg:text-7xl font-bold leading-[0.95] max-w-4xl mx-auto">
                        <T k="home.final.title">{`Bereit für\nden nächsten Hire?`}</T>
                    </h2>
                    <T k="home.final.subtitle" as="p" className="text-aimploy-muted text-lg mt-6 max-w-xl mx-auto">
                        30 Min. unverbindlich. Wir schauen uns Ihre Rolle an und zeigen, was wir können.
                    </T>
                    <Link
                        to="/kontakt"
                        data-testid="home-final-cta"
                        className="inline-flex items-center gap-2 mt-10 bg-aimploy-navy hover:bg-aimploy-navy-hover text-white px-10 py-5 rounded-full text-base font-semibold hover:scale-[1.02] active:scale-[0.98] transition-all"
                    >
                        <T k="home.final.cta">Erstgespräch buchen</T>
                        <ArrowUpRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
